import { Box } from '@mui/material';
import { Formik } from 'formik';
import { useMemo, useReducer, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import CustomAlert from 'components/shared/CustomAlert/CustomAlert';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import CustomStepper from 'components/shared/CustomStepper/CustomStepper';
import { initialAlertState, useAlertStatus } from 'customHook/useAlertStatus';
import { DealsDetailsType } from 'model/deals.model';

import { modalReducer } from 'reducers/modalReducer';
import RoutePaths from 'routes/RoutePaths';
import { DealsService } from 'service/deals.service';
import CancelModal from './CancelModal';
import { METADATA_FIELDS, steps } from './data';
import { coreDealInformation } from './fields.config';
import { muiStyles } from './muiStyles';

type DealFromProps = {
  isEdit: boolean;
  deal?: any;
};

export const DealForm = ({ isEdit, deal }: DealFromProps) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState<{ [k: number]: boolean }>({});
  const [modalState, dispatch] = useReducer(modalReducer, {
    cancel: false,
  });

  const { alertState, startOperation, completeOperation, setAlertState, getAlertContent } =
    useAlertStatus();

  const initialValues = useMemo<DealsDetailsType>(() => {
    if (isEdit) return deal;

    return {
      dealType: 'brandDeal',
      services: [{ serviceName: '', floor: 0, target: 0, ceil: 0 }],
    };
  }, [isEdit, deal]);

  const handleDealSubmit = async (values: DealsDetailsType) => {
    const { expectCloseDate, ...restDealDetails } = values;

    const updatableFields = { ...restDealDetails };

    METADATA_FIELDS.forEach((field) => {
      delete updatableFields[field as keyof typeof updatableFields];
    });

    Object.keys(updatableFields).forEach((key) => {
      const value = updatableFields[key as keyof typeof updatableFields];
      if (value === '') {
        delete updatableFields[key as keyof typeof updatableFields];
      }
    });

    const payload = {
      ...updatableFields,
      closeDate: restDealDetails.closeDate || expectCloseDate || undefined,
    };

    const operationType = isEdit && id ? 'update' : 'create';

    try {
      startOperation(operationType, isEdit && id ? 'Updating deal...' : 'Creating deal...');

      let response;
      if (isEdit && id) {
        try {
          response = await DealsService.updateDeal(id, payload);
        } catch (dealsError) {}
      } else {
        response = await DealsService.createDeal(payload);
      }

      completeOperation(operationType, `Deal ${isEdit ? 'updated' : 'created'} successfully`);
      navigate(`${RoutePaths.Deals}/${response.dealId}`);
    } catch (err: any) {
      completeOperation(
        operationType,
        err?.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} deal`,
        true,
      );
    }
  };

  const onCancelEdit = () => dispatch({ type: 'OPEN', payload: 'cancelEdit' });

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={async (values) => {
        await handleDealSubmit(values);
      }}
    >
      {({ values, handleSubmit }) => {
        const currentStep = steps[activeStep];

        const isStep0Valid = () => {
          const requiredFields = coreDealInformation
            .filter((item) => item.required)
            .map((item) => item.name);

          return requiredFields.every((fieldName) => {
            const value = values[fieldName];
            return value !== undefined && value !== null && value !== '';
          });
        };

        return (
          <>
            <Box sx={muiStyles.formRoot}>
              <Box sx={muiStyles.stepsHeader}>
                <CustomStepper
                  activeStep={activeStep}
                  steps={steps.map((s) => s.label)}
                  setActiveStep={setActiveStep}
                  setCompleted={setCompleted}
                  completed={completed}
                  disabled={activeStep === 0 || false}
                />
              </Box>

              <Box sx={muiStyles.stepContent}>
                <currentStep.Content />
              </Box>

              <Box sx={muiStyles.controls}>
                <CustomButton
                  text={'Cancel'}
                  onClick={onCancelEdit}
                  variant='outlined'
                  style={muiStyles.cancelButton}
                  disabled={false}
                />

                <Box sx={muiStyles.navigationControls}>
                  {activeStep !== 0 && (
                    <CustomButton
                      text={'Previous'}
                      onClick={() => setActiveStep((currentStep) => currentStep - 1)}
                      variant='outlined'
                      style={muiStyles.previousButton}
                      disabled={activeStep === 0}
                    />
                  )}

                  <CustomButton
                    text={activeStep === 0 ? 'Continue' : 'Save'}
                    onClick={
                      activeStep === 0
                        ? () => setActiveStep((currentStep) => currentStep + 1)
                        : handleSubmit
                    }
                    style={muiStyles.actionButton}
                    variant={'contained'}
                    disabled={!isStep0Valid()}
                  />
                </Box>
              </Box>
            </Box>

            <CustomAlert
              open={alertState.status !== 'idle'}
              handleClose={() => setAlertState(initialAlertState)}
              message={getAlertContent(alertState)}
              error={alertState.status === 'error'}
            />

            <CancelModal state={modalState.cancelEdit} dispatch={dispatch} />
          </>
        );
      }}
    </Formik>
  );
};
